/**
 * useRealtimeEvents — SSE hook for real-time cache invalidation
 *
 * Demande un ticket (60 s, usage unique) puis se connecte à GET /v1/events?ticket=<ticket> :
 * le jeton d'accès ne passe jamais dans l'adresse. Invalide les caches React Query
 * quand le serveur pousse un événement.
 *
 * Reconnects automatically with exponential backoff on disconnect.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '@/config';
import { api, getToken } from '@/services/api';

const INITIAL_RECONNECT_MS = 3_000;
const MAX_RECONNECT_MS = 60_000;

export function useRealtimeEvents(): void {
    const queryClient = useQueryClient();
    const esRef = useRef<EventSource | null>(null);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectDelayRef = useRef(INITIAL_RECONNECT_MS);
    const unmountedRef = useRef(false);

    useEffect(() => {
        unmountedRef.current = false;

        function scheduleReconnect() {
            if (unmountedRef.current) return;

            reconnectTimerRef.current = setTimeout(() => {
                if (!unmountedRef.current) void connect();
            }, reconnectDelayRef.current);

            // Exponential backoff, capped at MAX_RECONNECT_MS
            reconnectDelayRef.current = Math.min(
                reconnectDelayRef.current * 2,
                MAX_RECONNECT_MS,
            );
        }

        async function connect() {
            if (!getToken() || unmountedRef.current) return;

            // Un ticket ne sert qu'une fois : chaque (re)connexion en demande un neuf.
            let ticket: string;
            try {
                const { data } = await api.post<{ data: { ticket: string } }>('/events/ticket');
                ticket = data.data.ticket;
            } catch {
                scheduleReconnect();
                return;
            }
            if (unmountedRef.current) return;

            const es = new EventSource(`${config.apiUrl}/v1/events?ticket=${encodeURIComponent(ticket)}`);
            esRef.current = es;

            // Initial handshake — reset backoff
            es.addEventListener('connected', () => {
                reconnectDelayRef.current = INITIAL_RECONNECT_MS;
            });

            // Invalidation events from backend mutations
            es.onmessage = (e: MessageEvent<string>) => {
                try {
                    const { entities } = JSON.parse(e.data) as { entities?: string[] };
                    if (entities?.length) {
                        for (const entity of entities) {
                            queryClient.invalidateQueries({ queryKey: [entity] });
                        }
                    }
                } catch {
                    // Ignore malformed events
                }
            };

            // On error: close, wait with backoff, retry
            es.onerror = () => {
                es.close();
                esRef.current = null;
                scheduleReconnect();
            };
        }

        void connect();

        return () => {
            unmountedRef.current = true;
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            esRef.current?.close();
            esRef.current = null;
        };
    }, [queryClient]);
}
